import React, { useState } from "react";
import { Button, Container, Input, Modal, ModalBody, ModalFooter, FormGroup, Label, Col, Row, FormText } from "reactstrap";
import DataTable from "../../../components/DataTable";
import DialogBox from "../../../components/DialogBox";
import _ from 'lodash';
import { useTheme } from '../../../hooks/useTheme';
import CreatableSelect from 'react-select/creatable';
import { SelectInput } from '../../../components/DataFilter';
import { toast } from 'react-toastify';

const ItemCustomAttributes = ({ json, onChange }) => {
    const theme = useTheme();
    const [parsed, setParsed] = useState(() => {
        try {
            return typeof json === "string" ? JSON.parse(json) : json || [];
        } catch {
            return [];
        }
    });
    const [originalParsed, setOriginalParsed] = useState(() => {
        try {
            return typeof json === "string" ? JSON.parse(json) : json || [];
        } catch {
            return [];
        }
    });

    const [modalOpen, setModalOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [isDeleteDialogBoxOpen, setIsDeleteDialogBoxOpen] = useState(false);
    const [currentAttr, setCurrentAttr] = useState({ label: "", defaultValue: "", userCanEdit: false, required: false, fieldType: 'text' });
    const [isDialogBoxOpen, setIsDialogBoxOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [deleteId, setDeleteId] = useState(null);
    const [fullscreen, setFullscreen] = useState(false);

    const toggleFullscreen = () => setFullscreen((prev) => !prev);

    const customTheme = {
        ...theme,
        colors: {
            ...theme.colors,
            primary25: "#B2D953",
            primary: theme === "dark-mode" ? "#2c414b" : "#dee2e6",
            neutral0: theme === "dark-mode" ? "#1b272d" : "#E4E4E4",
        }
    };

    const openCreateModal = () => {
        setEditingIndex(null);
        setCurrentAttr({ label: "", defaultValue: "", userCanEdit: false, required: false, fieldType: 'text' });
        setModalOpen(true);
    };

    const openEditModal = (row, index) => {
        setEditingIndex(index);
        setCurrentAttr({ ...row });
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setCurrentAttr({ label: "", defaultValue: "", userCanEdit: false, required: false, fieldType: 'text' });
        setEditingIndex(null);
    };

    const handleSave = () => {
        const parsedJson = JSON.parse(json);
        if (parsedJson) {
            const count = originalParsed.filter(w => w.label === currentAttr.label).length;
            if (count > 0 && editingIndex === null) {
                toast.warning("This label already exists! Please select another.");
                return
            }
        }
        
        const updated = [...parsed];
        if (editingIndex !== null) {
            updated[editingIndex] = currentAttr;
        } else {
            updated.push(currentAttr);
        }
        setParsed(updated);
        closeModal();
        if (onChange) {
            onChange(JSON.stringify(updated));
        }
        setOriginalParsed(updated);
    };

    const handleDelete = () => {
        if (editingIndex === null && deleteId === null) return;
        let updated = [];
        if (editingIndex !== null)
            updated = parsed.filter((_, idx) => idx !== editingIndex);
        else if (deleteId !== null)
            updated = parsed.filter((row) => row.label !== deleteId);
        setParsed(updated);
        closeModal();
        if (onChange) {
            onChange(JSON.stringify(updated));
        }
        if (deleteId !== null) setDeleteId(null);
    };

    const handleClose = () => {
        if (!_.isEqual(parsed, originalParsed)) {
            openDialogBox();
        } else {
            setModalOpen(false);
        }
    }

    const openDialogBox = () => setIsDialogBoxOpen(true);
    const closeDialogBox = () => setIsDialogBoxOpen(false);

    const handleKeyDown = (event) => {
        if (!inputValue) return;
        switch (event.key) {
            case 'Enter':
            case 'Tab':
                setCurrentAttr({
                    ...currentAttr,
                    lookupChoices: [...(currentAttr.lookupChoices || []), inputValue]
                });
                setInputValue('');
                event.preventDefault();
                break;
            default:
                break;
        }
    };

    const handleDefaultValueChange = async (e) => {
        const { name, value, type, checked } = e.target;
        let newValue = type === 'checkbox' ? checked : value;

        setCurrentAttr(prevItem => ({
            ...prevItem,
            [name]: type === "checkbox" ? checked : newValue,
            defaultValue: name === 'fieldType' ? '' : name === 'defaultValue' ? newValue : prevItem.defaultValue,
            lookupChoices: type !== 'lookup' ? null : prevItem.lookupChoices
        }));
    }

    const handleTypeChange = async (fieldType) => {
        let defaultValue = '';
        switch(fieldType)
        {
            case "integer":
            case "decimal":
                defaultValue = 0;
                break;
            case "datetime":
                defaultValue = null;
                break;
            case "boolean":
                defaultValue = false;
                break;
            default:
                break;
        }

        const { lookupChoices, ...filtered } = currentAttr;
        setCurrentAttr({ ...filtered, fieldType: fieldType, defaultValue: defaultValue, required: false, userCanEdit: false })
    }

    const handleSelectChange = ({ name, value }) => {
        setCurrentAttr(prevItem => ({
            ...prevItem,
            [name]: value
        }));
    }

    const defaultValueComponent = () => {
        switch (currentAttr.fieldType) {
            case "text":
                return <Input type="text" name="defaultValue" id="defaultValue" value={currentAttr.defaultValue || ""} onChange={handleDefaultValueChange} />;
            case "integer":
                return <Input type="number" name="defaultValue" id="defaultValue" step="1" value={currentAttr.defaultValue || 0} onChange={handleDefaultValueChange} />;
            case "datetime":
                return <Input type="datetime-local" name="defaultValue" id="defaultValue" value={currentAttr.defaultValue || new Date()} onChange={handleDefaultValueChange} />;
            case "boolean":
                return <FormGroup switch><Input id="defaultValue" type="switch" role="switch" name="defaultValue" value={currentAttr.defaultValue || false} onChange={handleDefaultValueChange}></Input></FormGroup>;
            case "decimal":
                return <Input type="number" name="defaultValue" id="defaultValue" step="0.01" value={currentAttr.defaultValue || 0.00} onChange={handleDefaultValueChange} />;
            case "lookup":
                return <SelectInput placeholder="Select a choice..." name="defaultValue" defaultValue={(currentAttr.lookupChoices || []).map((option) => ({ label: option, value: option })).find(f => f.label === currentAttr.defaultValue) || ""} value={(currentAttr.lookupChoices || []).map((option) => ({ label: option, value: option })).find(f => f.label === currentAttr.defaultValue) || ""} options={(currentAttr.lookupChoices || []).map((option) => ({ label: option, value: option }))} onChange={(value) => handleSelectChange({ value, name: 'defaultValue' })} />;
            default:
                return <Input type="text" name="defaultValue" id="defaultValue" value={currentAttr.defaultValue || ""} onChange={handleDefaultValueChange} />;
        }
    }

    const columns = [
        { key: "label", label: "Label" },
        { key: "defaultValue", label: "Default Value", width: 100,
            render: (value, row) => row.fieldType !== "boolean" ? <span>{row.defaultValue}</span> : <Input type="checkbox" checked={row.defaultValue} disabled />
        },
        {
            key: "userCanEdit", label: "User can edit", width: 100,
            render: (value, row) => <Input type="checkbox" checked={row.userCanEdit} disabled />
        },
        {
            key: "required", label: "Required", width: 100,
            render: (value, row) => <Input type="checkbox" checked={row.required} disabled />
        },
        {
            key: "delete", label: "Actions", isSortDisabled: true, className: "text-end",
            render: (value, row) => {
                return <Button color="danger" size="sm" onClick={(e) => { setDeleteId(row.label); setIsDeleteDialogBoxOpen(true); }}><i className="bi bi-trash"></i></Button>
            }
        }
    ];

    return (
        <Container fluid>
            <DataTable
                columns={columns}
                data={parsed}
                isLoading={false}
                exportToCsv={false}
                searchableFields={["label", "value"]}
                actions={[
                    <Button key="create" onClick={openCreateModal}>
                        Create New Attribute
                    </Button>
                ]}
                onRowClick={openEditModal}
            />

            <Modal isOpen={modalOpen} toggle={closeModal} fullscreen={fullscreen} centered size="xl" scrollable backdrop="static">
                <div className="modal-header">
                    <h5 className="mb-0">{editingIndex !== null ? "Edit Attribute" : "New Attribute"}</h5>
                    <div className="d-flex gap-2">
                        <Button style={{ borderColor: 'transparent' }} color={theme === "dark-mode" ? "dark" : "light"} outline onClick={toggleFullscreen}><i className={"bi".concat(fullscreen ? " bi-fullscreen-exit" : " bi-arrows-fullscreen")}></i></Button>
                        <Button style={{ borderColor: 'transparent' }} color={theme === "dark-mode" ? "dark" : "light"} outline onClick={handleClose}><i className="bi bi-x"></i></Button>
                    </div>
                </div>
                <ModalBody>
                    <Row className="mb-3">
                        <Col sm={3}>
                            <Label style={{ marginBottom: 0 }}>Label</Label>
                        </Col>
                        <Col sm={9}>
                            <Input
                                type="text"
                                name="label"
                                id="label"
                                value={currentAttr.label || ""}
                                onChange={(e) => setCurrentAttr({ ...currentAttr, label: e.target.value })}
                                invalid={!currentAttr.label}
                            />
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col sm={3}>
                            <Label style={{ marginBottom: 0 }}>Type</Label>
                        </Col>
                        <Col sm={9}>
                            <Input
                                type="select"
                                name="fieldType"
                                id="fieldType"
                                value={currentAttr.fieldType || "text"}
                                onChange={(e) => handleTypeChange(e.target.value)}
                                invalid={!currentAttr.fieldType}
                            >
                                <option key="Text" value={'text'}>
                                    Text
                                </option>
                                <option key="Integer" value={'integer'}>
                                    Integer
                                </option>
                                <option key="Datetime" value={'datetime'}>
                                    Date/Time
                                </option>
                                <option key="Boolean" value={'boolean'}>
                                    Boolean
                                </option>
                                <option key="Decimal" value={'decimal'}>
                                    Decimal
                                </option>
                                <option key="Lookup" value={'lookup'}>
                                    Lookup
                                </option>
                            </Input>
                        </Col>
                    </Row>
                    {currentAttr.fieldType === 'lookup' &&
                        <Row className="mb-3">
                            <Col sm={3}>
                                <Label style={{ marginBottom: 0 }}>Choices</Label>
                            </Col>
                            <Col sm={9}>
                                <CreatableSelect
                                    components={{ DropdownIndicator: null }}
                                    inputValue={inputValue}
                                    isClearable
                                    isMulti
                                    theme={customTheme}
                                    menuIsOpen={false}
                                    onChange={(newValue) => setCurrentAttr({
                                        ...currentAttr,
                                        lookupChoices: (newValue || []).map((option) => option.label)
                                    })}
                                    onInputChange={(newValue) => setInputValue(newValue)}
                                    onKeyDown={handleKeyDown}
                                    value={(currentAttr.lookupChoices || []).map((option) => ({ label: option, value: option }))}
                                />
                            </Col>
                            <Col sm={3} />
                            <Col sm={9}>
                                <FormText>
                                    <i className="bi bi-info-circle-fill me-2"></i>
                                    Type something and press enter...
                                </FormText>
                            </Col>
                        </Row>
                    }
                    <Row className="mb-3">
                        <Col sm={3}>
                            <Label style={{ marginBottom: 0 }}>Default Value</Label>
                        </Col>
                        <Col sm={9}>
                            {defaultValueComponent()}
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={3}>
                            <Label style={{ marginBottom: 0 }}>Required</Label>
                        </Col>
                        <Col sm={9}>
                            <FormGroup switch>
                                <Input id="switch" type="switch" role="switch" name="required" checked={currentAttr.required} onChange={(e) => setCurrentAttr({ ...currentAttr, required: e.target.checked })}></Input>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={3}>
                            <Label style={{ marginBottom: 0 }}>User can edit</Label>
                        </Col>
                        <Col sm={9}>
                            <FormGroup switch>
                                <Input id="switch" type="switch" role="switch" name="userCanEdit" checked={currentAttr.userCanEdit} onChange={(e) => setCurrentAttr({ ...currentAttr, userCanEdit: e.target.checked })}></Input>
                            </FormGroup>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter style={{ justifyContent: 'space-between' }}>
                    <div>
                        {editingIndex !== null && <Button outline onClick={() => setIsDeleteDialogBoxOpen(true)} color="danger">Delete</Button>}
                    </div>
                    <div className="gap-2 d-flex">
                        <Button outline onClick={handleClose} color={theme === "dark-mode" ? "dark" : "light"}>Close</Button>
                        <Button color="secondary" onClick={() => handleSave()}>Save</Button>
                    </div>
                </ModalFooter>
            </Modal>
            <DialogBox
                isOpen={isDeleteDialogBoxOpen}
                title="Are you sure you want to delete?"
                message={
                    <div className="d-flex align-items-center">
                        <i className="bi bi-x-circle-fill text-danger fs-1 me-3"></i>
                        <span>Do you want to delete this attribute?</span>
                    </div>
                }
                toggle={() => setIsDeleteDialogBoxOpen(false)}
                buttons={[
                    {
                        label: "No",
                        color: "secondary",
                        onClick: () => setIsDeleteDialogBoxOpen(false)
                    },
                    {
                        label: "Yes",
                        color: "primary",
                        onClick: () => {
                            handleDelete(); setIsDeleteDialogBoxOpen(false);
                        }
                    }
                ]}
            />
            <DialogBox
                isOpen={isDialogBoxOpen}
                title="Save Changes?"
                message={
                    <div className="d-flex align-items-center">
                        <i className="bi bi-exclamation-triangle-fill text-warning fs-1 me-3"></i>
                        <span>You have unsaved changes. <br />Save changes before closing?</span>
                    </div>
                }
                toggle={closeDialogBox}
                buttons={[
                    {
                        label: 'Yes',
                        color: 'primary',
                        // icon: 'bi bi-check2-circle',
                        onClick: () => { handleSave(); closeDialogBox(); }
                    },
                    {
                        label: 'No',
                        color: 'secondary',
                        // icon: 'bi bi-x-circle',
                        onClick: () => { closeDialogBox(); closeModal(); }
                    },
                    {
                        label: 'Cancel',
                        color: 'secondary',
                        onClick: () => { closeDialogBox(); }
                    }
                ]}
            />
        </Container>
    );
};

export default ItemCustomAttributes;
